import type { WidgetAppConfig } from '@pypestream/pype/customization';

import type { Channel } from '../utils';
import type {
  PypeBeta,
  PypeStyle,
} from '../xstate/prostudio.api-service.types';
import type { MessageKey, PlatformMessages } from './default-messages';

/**
 * Project Bobcat — "Copy settings…" between two channels of one solution.
 *
 * Two things can be copied, each through the save path its own editor uses:
 *
 * - Default messages live on the channel's PYPE (`pype.autoresponders`), saved
 *   with `updateDefaultMessages { id: pypeId, env }`. Two channels on the same
 *   pype already share one set, so that pair can't copy messages.
 * - Styling lives on the channel (widget) itself, in two stores:
 *   1. Channel Settings' branding: the widget's `style`, `beta.customStyling`
 *      and `custom_pype_css`, saved with `updateChannel`. `mutatePutWidget*`
 *      REPLACES all three, so the target channel is spread through and only
 *      the fields below change (same approach as the Pype Designer's rename).
 *   2. The Pype Next designer's widget app design (`WidgetAppConfig`), saved
 *      with `mutateWidgetAppDesign` — only when the `pype-next` flag is on.
 *
 * Copied is look and feel only. Never copied: names and text labels (the
 * widget's display name, launcher name, the design's display name), the logo
 * (stored on the pype, uploaded through its own flow), and anything that
 * identifies or connects the channel (ids, app id, bot binding, live-agent
 * routing, environment, URLs and keys).
 */

/** `PypeStyle` fields copied — every field except the display `name`. */
export const COPIED_STYLE_FIELDS = [
  'widgetPosition',
  'shouldLockRatio',
  'widgetHeight',
  'widgetWidth',
  'widgetStyleColor',
  'widgetStyleTextColor',
  'agentBubbleColor',
  'agentBubbleTextColor',
  'userBubbleColor',
  'userBubbleTextColor',
  'bannerUrl',
] as const satisfies readonly (keyof PypeStyle)[];

/** `beta.customStyling` fields copied — everything but `launcherName`. */
export const COPIED_CUSTOM_STYLING_FIELDS = [
  'buttonColor',
  'textColor',
  'brandMomentUrl',
] as const satisfies readonly (keyof PypeBeta['customStyling'])[];

/** Pype Next `branding` fields copied — images, not text labels. */
export const COPIED_DESIGN_BRANDING_FIELDS = [
  'customerLogoUrl',
  'bannerUrl',
  'brandMomentUrl',
] as const satisfies readonly (keyof WidgetAppConfig['branding'])[];

const STYLE_LABELS: Record<(typeof COPIED_STYLE_FIELDS)[number], string> = {
  widgetPosition: 'Widget position',
  shouldLockRatio: 'Lock aspect ratio',
  widgetHeight: 'Widget height',
  widgetWidth: 'Widget width',
  widgetStyleColor: 'Header color',
  widgetStyleTextColor: 'Header text color',
  agentBubbleColor: 'Agent bubble color',
  agentBubbleTextColor: 'Agent bubble text color',
  userBubbleColor: 'User bubble color',
  userBubbleTextColor: 'User bubble text color',
  bannerUrl: 'Banner image',
};

const CUSTOM_STYLING_LABELS: Record<
  (typeof COPIED_CUSTOM_STYLING_FIELDS)[number],
  string
> = {
  buttonColor: 'Button color',
  textColor: 'Button text color',
  brandMomentUrl: 'Brand moment image',
};

const MESSAGE_LABELS: Record<MessageKey, string> = {
  offline: 'Agents offline',
  busy: 'Agents busy',
  technicalError: 'Technical error',
};

export interface SettingChange {
  label: string;
  from: string;
  to: string;
}

const show = (value: unknown): string => {
  if (value === undefined || value === null || value === '') return '(empty)';
  if (typeof value === 'boolean') return value ? 'On' : 'Off';
  return String(value);
};

type LegacyStyling = Pick<Channel, 'style' | 'beta' | 'customPypeCss'>;

/**
 * The target's legacy branding with the source's look and feel copied over.
 * Target fields that aren't copied (its display name, launcher name) are kept.
 */
export const copyLegacyStyling = (
  source: LegacyStyling,
  target: LegacyStyling
): Required<LegacyStyling> => {
  const style = { ...(target.style ?? {}) } as PypeStyle;
  COPIED_STYLE_FIELDS.forEach((field) => {
    if (source.style && field in source.style) {
      (style as Record<string, unknown>)[field] = source.style[field];
    }
  });
  if (style.name === undefined) style.name = '';

  const targetCustom = target.beta?.customStyling;
  const customStyling = {
    ...(targetCustom ?? {}),
  } as PypeBeta['customStyling'];
  COPIED_CUSTOM_STYLING_FIELDS.forEach((field) => {
    const value = source.beta?.customStyling?.[field];
    if (value !== undefined) customStyling[field] = value;
  });

  return {
    style,
    beta: { ...(target.beta ?? {}), customStyling } as PypeBeta,
    customPypeCss: source.customPypeCss ?? '',
  };
};

/** What `copyLegacyStyling` would change on the target, for the confirm step. */
export const diffLegacyStyling = (
  source: LegacyStyling,
  target: LegacyStyling
): SettingChange[] => {
  const next = copyLegacyStyling(source, target);
  const changes: SettingChange[] = [];

  COPIED_STYLE_FIELDS.forEach((field) => {
    const from = target.style?.[field];
    const to = next.style[field];
    if (show(from) !== show(to)) {
      changes.push({
        label: STYLE_LABELS[field],
        from: show(from),
        to: show(to),
      });
    }
  });
  COPIED_CUSTOM_STYLING_FIELDS.forEach((field) => {
    const from = target.beta?.customStyling?.[field];
    const to = next.beta.customStyling?.[field];
    if (show(from) !== show(to)) {
      changes.push({
        label: CUSTOM_STYLING_LABELS[field],
        from: show(from),
        to: show(to),
      });
    }
  });
  if ((target.customPypeCss ?? '') !== next.customPypeCss) {
    const lines = (css: string) =>
      css ? `${css.split('\n').length} line(s)` : '(empty)';
    changes.push({
      label: 'Custom CSS',
      from: lines(target.customPypeCss ?? ''),
      to: lines(next.customPypeCss),
    });
  }
  return changes;
};

/**
 * The target's Pype Next design with the source's appearance copied over:
 * theme, tokens, part overrides, raw CSS, shell (modality, size) and the
 * branding images. The target's display name and launcher name stay.
 */
export const copyPypeNextDesign = (
  source: WidgetAppConfig,
  target: WidgetAppConfig
): WidgetAppConfig => {
  const branding = { ...target.branding };
  // Generic per field, so each copy keeps that field's own type.
  const copyField = <K extends keyof WidgetAppConfig['branding']>(field: K) => {
    branding[field] = source.branding[field];
  };
  COPIED_DESIGN_BRANDING_FIELDS.forEach(copyField);
  return {
    ...target,
    design: structuredClone(source.design),
    shell: structuredClone(source.shell),
    branding,
  };
};

/** The source's messages, as-is, become the target pype's messages. */
export const diffMessages = (
  source: PlatformMessages,
  target: PlatformMessages
): SettingChange[] =>
  (Object.keys(MESSAGE_LABELS) as MessageKey[]).flatMap((key) => {
    const changes: SettingChange[] = [];
    if (source[key].text !== target[key].text) {
      changes.push({
        label: MESSAGE_LABELS[key],
        from: show(target[key].text),
        to: show(source[key].text),
      });
    }
    if (source[key].enable !== target[key].enable) {
      changes.push({
        label: `${MESSAGE_LABELS[key]} (on/off)`,
        from: show(target[key].enable),
        to: show(source[key].enable),
      });
    }
    return changes;
  });

export interface CopyAvailability {
  available: boolean;
  /** Why not, when it isn't. */
  reason?: string;
}

/** Whether default messages can be copied from `source` to `target`. */
export const messagesAvailability = (
  source: Pick<Channel, 'pypeId'>,
  target: Pick<Channel, 'pypeId'>,
  hasMessages: (pypeId: string) => boolean
): CopyAvailability => {
  if (source.pypeId === target.pypeId) {
    return {
      available: false,
      reason:
        'Both channels are on the same pype, so they already share messages.',
    };
  }
  if (!hasMessages(source.pypeId) || !hasMessages(target.pypeId)) {
    return {
      available: false,
      reason: 'No default messages found for one of these channels.',
    };
  }
  return { available: true };
};

/** Whether styling can be copied from `source` to `target`. */
export const stylingAvailability = (
  source: Pick<Channel, 'type'>,
  target: Pick<Channel, 'type'>
): CopyAvailability =>
  source.type === 'twilio_voice' || target.type === 'twilio_voice'
    ? { available: false, reason: 'Voice channels have no widget styling.' }
    : { available: true };
